// Employee Attendance — monthly summary list screen.
//
// Mirrors cycleCount.tsx's screen shape: owns {searchText, offset, limit,
// page} in state (here extended with month/year), dispatches
// getAttendanceListAction, renders AttendanceTable inside the shared
// DataTable with the same pagination={{page, total, onChange}} contract.
// "Mark Attendance" navigates to the calendar detail screen for that
// employee (deliverable #1).
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deepCopyFunction } from "../../Utils/helpers";
import { getAttendanceListAction, getAttendanceRecordByIdAction } from "../../Store/actions/apparelAction";
import { LoadingOverlay } from "../../shared/loading-overlay";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import AttendanceTable from "./AttendanceTable";

// Fiscal-year month order (April -> March), each paired with its actual
// calendar month number.
const FISCAL_MONTHS = [
  { name: "April", value: 4 },
  { name: "May", value: 5 },
  { name: "June", value: 6 },
  { name: "July", value: 7 },
  { name: "August", value: 8 },
  { name: "September", value: 9 },
  { name: "October", value: 10 },
  { name: "November", value: 11 },
  { name: "December", value: 12 },
  { name: "January", value: 1 },
  { name: "February", value: 2 },
  { name: "March", value: 3 },
];

// Accounting year runs April - March; the filter only ever needs to cover
// the *current* accounting year, shown as a single "2026-2027"-style option
// rather than its two constituent calendar years.
const getAccountingYearBounds = () => {
  const now = new Date();
  const calendarMonth = now.getMonth() + 1; // 1-based
  const calendarYear = now.getFullYear();
  const startYear = calendarMonth >= 4 ? calendarYear : calendarYear - 1;
  return { startYear, endYear: startYear + 1 };
};

// Jan/Feb/Mar fall in the second calendar year of the accounting year;
// Apr-Dec fall in the first.
const calendarYearForMonth = (fiscalStartYear: number, month: number) =>
  month >= 4 ? fiscalStartYear : fiscalStartYear + 1;

const AttendanceList = () => {
  const now = new Date();
  const { startYear: currentFiscalStartYear } = getAccountingYearBounds();
  const filterTemp: any = {
    searchText: "",
    month: now.getMonth() + 1, // 1-based, matches viewAttendanceListInput.month: Int!
    fiscalStartYear: currentFiscalStartYear,
    offset: 0,
    limit: 10,
    page: 1,
  };

  const [filterData, setFilterData] = useState<any>(deepCopyFunction(filterTemp));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getAttendanceListData, getAttendanceListLoad, getAttendanceRecordByIdData } = apparelReducerState;

  // Arriving from Audit Log: resolve the attendance record's own uniqueKey
  // back to its employee + date (no field on this list can search for it
  // directly), then jump straight to that employee's Mark Attendance sheet,
  // on that day.
  useEffect(() => {
    if (location.state?.auditRecordId) {
      dispatch(getAttendanceRecordByIdAction({ recordId: location.state.auditRecordId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const record = getAttendanceRecordByIdData?.getAttendanceRecordById;
    if (!location.state?.auditRecordId || !record?.employeeId) return;
    navigate(`/attendance/${record.employeeId}`, {
      replace: true,
      state: { employeeName: record.employeeName, highlightDate: record.attendanceDate },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAttendanceRecordByIdData]);

  const attendanceList = getAttendanceListData?.getAttendanceList?.attendanceList;
  const attendancePage = getAttendanceListData?.getAttendanceList?.page;

  const getAttendanceList = () => {
    const payload = {
      searchText: filterData?.searchText,
      month: filterData?.month,
      year: calendarYearForMonth(filterData?.fiscalStartYear, filterData?.month),
      limit: filterData?.limit,
      offset: filterData?.offset,
    };
    dispatch(getAttendanceListAction(payload));
  };

  useEffect(() => {
    if (filterData?.searchText?.length >= 0) {
      getAttendanceList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData?.searchText, filterData?.month, filterData?.fiscalStartYear, filterData?.offset]);

  const getSearchTextDataList = (searchText: any) => {
    setFilterData({ ...filterData, searchText, offset: 0, page: 1 });
  };

  // DataTable gives a 1-based page; translate to the legacy offset math
  // (same as cycleCount.tsx's onPageChange).
  const onPageChange = (nextPage: number) => {
    const limit = filterData?.limit || 10;
    const offset = (nextPage * limit) - limit === 0 ? 0 : (nextPage * limit) - limit;
    setFilterData({ ...filterData, page: nextPage, offset });
  };

  const onMonthChange = (value: string) => {
    setFilterData({ ...filterData, month: parseInt(value, 10), offset: 0, page: 1 });
  };

  const onFiscalYearChange = (value: string) => {
    setFilterData({ ...filterData, fiscalStartYear: parseInt(value, 10), offset: 0, page: 1 });
  };

  const onMarkAttendance = (record: any) => {
    // Carry the list's currently-selected month/year across so Mark
    // Attendance opens on the same period the user was looking at, instead
    // of always defaulting to the current calendar month.
    navigate(`/attendance/${record?.employeeId}`, {
      state: {
        employeeName: record?.employeeName,
        month: filterData?.month,
        fiscalStartYear: filterData?.fiscalStartYear,
      },
    });
  };

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <AttendanceTable
        data={attendanceList}
        loading={getAttendanceListLoad}
        onMarkAttendance={onMarkAttendance}
        searchValue={filterData?.searchText}
        onSearch={getSearchTextDataList}
        searchPlaceholder="Search By Employee Name/ID"
        title="Attendance"
        toolbar={
          <div className="flex items-center gap-2">
            <Select value={String(filterData?.month)} onValueChange={onMonthChange}>
              <SelectTrigger className="h-9 w-[140px]">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {FISCAL_MONTHS.map(({ name, value }) => (
                  <SelectItem key={name} value={String(value)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(filterData?.fiscalStartYear)} onValueChange={onFiscalYearChange}>
              <SelectTrigger className="h-9 w-[110px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={String(currentFiscalStartYear)}>
                  {currentFiscalStartYear}-{currentFiscalStartYear + 1}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        pagination={{
          page: attendancePage?.currentPage || filterData?.page || 1,
          total: attendancePage?.totalCount || 0,
          onChange: onPageChange,
        }}
      />
      <LoadingOverlay loading={getAttendanceListLoad} />
    </div>
  );
};

export default AttendanceList;
